// src/components/tasks/SubtaskCreateDialog.tsx
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Paperclip, X } from "lucide-react"
import { useState } from "react"
import { toast } from "@/hooks/use-toast"
import { useAuthStore } from "@/store/authStore"
import { db, storage } from "@/lib/firebase"
import { addDoc, collection, serverTimestamp, updateDoc, doc, arrayUnion } from "firebase/firestore"
import { uploadBytesResumable, getDownloadURL, ref as storageRef } from "firebase/storage"
import { useCalendarEventStore } from "@/store/calendarEventStore"

// === ДИЗАЙН-СЛОЙ ===
import { subtaskCreateTheme } from "./ui/SubtaskCreateDialogUI"

const employees = [
  { id: 1, name: "Андреев Алексей Евгеньевич" },
  { id: 2, name: "Багаутдинов Ахмет Айратович" },
  { id: 3, name: "Барладян Ирина Алексеевна" },
  { id: 4, name: "Павлик Кристина Валентиновна" },
  { id: 5, name: "Султанова Диана Рашитовна" },
  { id: 6, name: "Казарин Сергей Константинович" },
  { id: 7, name: "Степанушкин Сергей Вадимович" },
]

interface SubtaskCreateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  parentId: string
  onCreate: () => void
}

export function SubtaskCreateDialog({ open, onOpenChange, parentId, onCreate }: SubtaskCreateDialogProps) {
  const { user } = useAuthStore()
  const { addEvent } = useCalendarEventStore()

  const [newSubtask, setNewSubtask] = useState({
    title: "",
    description: "",
    assigneeId: 0,
    coAssigneeIds: [] as number[],
    observerIds: [] as number[],
    deadline: "",
    urgency: "planned" as "urgent" | "planned",
    difficulty: "medium" as "easy" | "medium" | "hard",
    reminderInterval: "",
  })
  const [newSubtaskFile, setNewSubtaskFile] = useState<File | null>(null)
  const userId = user ? Number(user.id) : 0

  const canAssignTo = (assigneeId: number): boolean => {
    if (assigneeId === userId) return true
    if (userId === 1) return true
    if (userId === 2) return assigneeId !== 1
    if (userId === 3 || userId === 4) return [3, 4, 5, 6, 7].includes(assigneeId)
    return false
  }

  const uploadFileAndGetData = async (file: File | null) => {
    if (!file) return null
    try {
      const fileRef = storageRef(storage, `tasks/${Date.now()}_${file.name}`)
      const uploadTask = uploadBytesResumable(fileRef, file)
      await new Promise((resolve, reject) => {
        uploadTask.on("state_changed", null, reject, resolve)
      })
      const url = await getDownloadURL(uploadTask.snapshot.ref)
      return { name: file.name, url }
    } catch (error) {
      toast({ title: "Ошибка загрузки файла", variant: "destructive" })
      throw error
    }
  }

  const createSubtask = async () => {
    if (!user || !newSubtask.title || newSubtask.assigneeId === 0 || !newSubtask.deadline) {
      toast({ title: "Ошибка", description: "Заполните все обязательные поля", variant: "destructive" })
      return
    }

    if (!canAssignTo(newSubtask.assigneeId)) {
      toast({ title: "Ошибка", description: "Вы не можете ставить подзадачу этому сотруднику", variant: "destructive" })
      return
    }

    if (newSubtask.coAssigneeIds) {
      for (const coId of newSubtask.coAssigneeIds) {
        if (!canAssignTo(coId)) {
          toast({ title: "Ошибка", description: "Вы не можете назначить этого соисполнителя", variant: "destructive" })
          return
        }
        if (coId === newSubtask.assigneeId) {
          toast({ title: "Ошибка", description: "Соисполнитель не может быть тем же, что и исполнитель", variant: "destructive" })
          return
        }
      }
    }

    if (newSubtask.observerIds?.length > 0) {
      for (const obsId of newSubtask.observerIds) {
        if (obsId === userId) {
          toast({ title: "Ошибка", description: "Нельзя добавить себя в наблюдатели", variant: "destructive" })
          return
        }
      }
    }

    const fileData = await uploadFileAndGetData(newSubtaskFile)

    const coAssignees = newSubtask.coAssigneeIds
      .map((id: number) => employees.find((e) => e.id === id)?.name || "")
      .filter(Boolean)

    const observers = newSubtask.observerIds
      .map((id: number) => employees.find((e) => e.id === id)?.name || "")
      .filter(Boolean)

    const isSelfTask = newSubtask.assigneeId === userId

    try {
      const subtaskRef = await addDoc(collection(db, "tasks"), {
        title: newSubtask.title,
        description: newSubtask.description,
        assigneeId: newSubtask.assigneeId,
        assignee: employees.find((e) => e.id === newSubtask.assigneeId)?.name || "",
        coAssigneeIds: newSubtask.coAssigneeIds || [],
        coAssignees,
        observerIds: newSubtask.observerIds || [],
        observers,
        urgency: newSubtask.urgency,
        difficulty: newSubtask.difficulty,
        reminderInterval: newSubtask.reminderInterval,
        deadline: newSubtask.deadline,
        status: "new",
        approvalStatus: "new",
        approvalCount: 0,
        file: fileData,
        acceptedByExecutor: false,
        approvedBy: [],
        approvedByCreator: false,
        isBlock: false,
        parentId: parentId,
        createdAt: serverTimestamp(),
        createdBy: user.name,
        createdById: user.id,
        creatorId: user.id,
        isSelfTask,
        history: [
          {
            userId: user.id,
            userName: user.name,
            action: "created",
            date: new Date().toISOString(),
          },
        ],
        messages: [],
      })

      await updateDoc(doc(db, "tasks", subtaskRef.id), {
        unreadBy: arrayUnion(
          newSubtask.assigneeId,
          ...newSubtask.coAssigneeIds,
          ...newSubtask.observerIds
        )
      })

      toast({ 
        title: isSelfTask ? "Своя подзадача создана!" : "Подзадача создана!" 
      })
      onCreate()

      if (newSubtask.deadline) {
        try {
          await addEvent({
            date: newSubtask.deadline,
            title: newSubtask.title,
            type: "task",
            description: newSubtask.description || "",
            createdBy: user.id.toString(),
            sourceTaskId: subtaskRef.id,
          })
          toast({ title: "Подзадача добавлена в календарь" })
        } catch (calendarErr) {
          console.error("Ошибка добавления подзадачи в календарь:", calendarErr)
          toast({
            title: "Подзадача создана, но не добавлена в календарь",
            description: "Проверьте консоль",
            variant: "destructive",
          })
        }
      }

      setNewSubtask({
        title: "",
        description: "",
        assigneeId: 0,
        coAssigneeIds: [],
        observerIds: [],
        deadline: "",
        urgency: "planned",
        difficulty: "medium",
        reminderInterval: "",
      })
      setNewSubtaskFile(null)
      onOpenChange(false)
    } catch (error) {
      console.error("Ошибка создания подзадачи:", error)
      toast({ title: "Ошибка", description: "Не удалось создать подзадачу", variant: "destructive" })
    }
  }

  const addCoAssignee = (coId: number) => {
    if (!newSubtask.coAssigneeIds.includes(coId) && coId !== newSubtask.assigneeId) {
      setNewSubtask({ ...newSubtask, coAssigneeIds: [...newSubtask.coAssigneeIds, coId] })
    }
  }

  const removeCoAssignee = (coId: number) => {
    setNewSubtask({ ...newSubtask, coAssigneeIds: newSubtask.coAssigneeIds.filter(id => id !== coId) })
  }

  const addObserver = (obsId: number) => {
    if (!newSubtask.observerIds.includes(obsId) && obsId !== userId) {
      setNewSubtask({ ...newSubtask, observerIds: [...newSubtask.observerIds, obsId] })
    }
  }

  const removeObserver = (obsId: number) => {
    setNewSubtask({ ...newSubtask, observerIds: newSubtask.observerIds.filter(id => id !== obsId) })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={subtaskCreateTheme.dialogContent}>
        <DialogHeader>
          <DialogTitle className="text-2xl">Новая подзадача</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Наименование подзадачи</Label>
            <Input
              value={newSubtask.title}
              onChange={(e) => setNewSubtask({ ...newSubtask, title: e.target.value })}
              className={subtaskCreateTheme.formInput}
            />
          </div>

          <div>
            <Label>Подробное описание</Label>
            <Textarea
              value={newSubtask.description}
              onChange={(e) => setNewSubtask({ ...newSubtask, description: e.target.value })}
              className={subtaskCreateTheme.formTextarea}
              rows={4}
            />
          </div>

          <div>
            <Label>Исполнитель</Label>
            <Select onValueChange={(v) => setNewSubtask({ ...newSubtask, assigneeId: +v })}>
              <SelectTrigger className={subtaskCreateTheme.selectTrigger}>
                <SelectValue placeholder="Выберите сотрудника" />
              </SelectTrigger>
              <SelectContent className={subtaskCreateTheme.selectContent}>
                {employees
                  .filter((e) => canAssignTo(e.id))
                  .map((e) => (
                    <SelectItem 
                      key={e.id} 
                      value={e.id.toString()} 
                      className="hover:bg-purple-800/60 text-purple-200"
                    >
                      {e.name}
                      {e.id === userId && (
                        <span className="ml-2 text-emerald-400 text-xs font-medium">(себе)</span>
                      )}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Соисполнители (не обязательно)</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {newSubtask.coAssigneeIds.map((coId) => {
                const emp = employees.find(e => e.id === coId)
                return (
                  <Badge key={coId} className={subtaskCreateTheme.badgeCoAssignee}>
                    {emp?.name}
                    <button onClick={() => removeCoAssignee(coId)} className="ml-2">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )
              })}
            </div>
            <Select onValueChange={(v) => addCoAssignee(+v)}>
              <SelectTrigger className={subtaskCreateTheme.selectTrigger}>
                <SelectValue placeholder="Добавить соисполнителя" />
              </SelectTrigger>
              <SelectContent className={subtaskCreateTheme.selectContent}>
                {employees
                  .filter((e) => canAssignTo(e.id) && e.id !== newSubtask.assigneeId && !newSubtask.coAssigneeIds.includes(e.id))
                  .map((e) => (
                    <SelectItem key={e.id} value={e.id.toString()} className="hover:bg-purple-800/60 text-purple-200">
                      {e.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Наблюдатели (не обязательно)</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {newSubtask.observerIds.map((obsId) => {
                const emp = employees.find(e => e.id === obsId)
                return (
                  <Badge key={obsId} variant="secondary" className={subtaskCreateTheme.badgeObserver}>
                    {emp?.name}
                    <button onClick={() => removeObserver(obsId)} className="ml-2">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )
              })}
            </div>
            <Select onValueChange={(v) => addObserver(+v)}>
              <SelectTrigger className={subtaskCreateTheme.selectTrigger}>
                <SelectValue placeholder="Добавить наблюдателя" />
              </SelectTrigger>
              <SelectContent className={subtaskCreateTheme.selectContent}>
                {employees
                  .filter((e) => e.id !== userId && !newSubtask.observerIds.includes(e.id))
                  .map((e) => (
                    <SelectItem key={e.id} value={e.id.toString()} className="hover:bg-purple-800/60 text-purple-200">
                      {e.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Срочность</Label>
            <Select onValueChange={(v) => setNewSubtask({ ...newSubtask, urgency: v as "urgent" | "planned" })}>
              <SelectTrigger className={subtaskCreateTheme.selectTrigger}>
                <SelectValue placeholder="Планово" />
              </SelectTrigger>
              <SelectContent className={subtaskCreateTheme.selectContent}>
                <SelectItem value="planned" className="hover:bg-purple-800/60 text-purple-200">Планово</SelectItem>
                <SelectItem value="urgent" className="hover:bg-purple-800/60 text-purple-200">Срочно</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Уровень сложности</Label>
            <Select onValueChange={(v) => setNewSubtask({ ...newSubtask, difficulty: v as "easy" | "medium" | "hard" })}>
              <SelectTrigger className={subtaskCreateTheme.selectTrigger}>
                <SelectValue placeholder="Средняя" />
              </SelectTrigger>
              <SelectContent className={subtaskCreateTheme.selectContent}>
                <SelectItem value="easy" className="hover:bg-purple-800/60 text-purple-200">Легкая</SelectItem>
                <SelectItem value="medium" className="hover:bg-purple-800/60 text-purple-200">Средняя</SelectItem>
                <SelectItem value="hard" className="hover:bg-purple-800/60 text-purple-200">Сложная</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Напоминание</Label>
            <Input
              value={newSubtask.reminderInterval}
              onChange={(e) => setNewSubtask({ ...newSubtask, reminderInterval: e.target.value })}
              placeholder="например: 1 день до дедлайна"
              className={subtaskCreateTheme.formInput}
            />
          </div>

          <div>
            <Label>Срок исполнения (дедлайн)</Label>
            <Input
              type="date"
              value={newSubtask.deadline}
              onChange={(e) => setNewSubtask({ ...newSubtask, deadline: e.target.value })}
              className={subtaskCreateTheme.formInput}
            />
          </div>

          <div>
            <Label>Прикрепить файл</Label>
            <label className="cursor-pointer block">
              <input
                type="file"
                className="hidden"
                onChange={(e) => setNewSubtaskFile(e.target.files?.[0] || null)}
              />
              <div className={subtaskCreateTheme.fileUploadArea}>
                <Paperclip className="h-5 w-5" />
                <span className="text-sm">
                  {newSubtaskFile ? newSubtaskFile.name : "Выбрать файл"}
                </span>
              </div>
            </label>
            {newSubtaskFile && (
              <button
                onClick={() => setNewSubtaskFile(null)}
                className="text-red-400 text-sm mt-2 block"
              >
                Удалить файл
              </button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button onClick={createSubtask} className={subtaskCreateTheme.createButton}>
            Создать подзадачу
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}